/** The `facts` area: what the machine computes about an item (domain/facts.ts)
 *  and the rules that grade it (domain/fact-rules.ts), in the words every
 *  surface shares: a rule's fact word, its condition, a statement's line and
 *  its provenance. */
export const facts = {
  /** Each fact kind's word in a rule (`FACT_RULE_WORD`). */
  'facts.word.quiet': 'quiet',
  'facts.word.endPassed': 'late',
  'facts.word.startPassed': 'start passed',
  'facts.word.noRequester': 'no requester',
  'facts.word.needsDoing': 'needs doing',
  'facts.word.dependents': 'has dependents',
  'facts.word.backPressure': 'ready dependents waiting',
  'facts.word.committedButInert': 'committed but inert',
  'facts.word.runningOnOldIntent': 'running on old intent',
  'facts.word.unattendedRisk': 'unattended risk',
  'facts.word.scheduleConflict': 'schedule conflict',
  /** The two conditions a flow's rule may name (docs/THRESHOLDS.md §2). */
  'facts.word.tagged': 'carries a tag',
  'facts.word.inStage': 'stands in a stage',
  /** A rule's condition with its threshold: "quiet past 14 days". */
  'facts.condition.pastDays': { one: '{fact} past {count} day', other: '{fact} past {count} days' },
  /** A condition rule's line: the tag it names, or the stage's label. */
  'facts.condition.tagged': 'carries #{tag}',
  'facts.condition.inStage': 'in {stage}',
  /** A declared day inside a line: the canonical ISO day, which the statement
   *  label shows as a date in the reader's locale. */
  'facts.day': '{day}',
  /** A fact's line (domain/facts.ts `factLine`). */
  'facts.line.quiet': { one: 'Inactive {count} day', other: 'Inactive {count} days' },
  'facts.line.endPassed': {
    one: '{count} day late, end date {end}',
    other: '{count} days late, end date {end}',
  },
  'facts.line.startPassed': 'Not started, start date {start}',
  'facts.line.noRequester': 'No requester',
  'facts.line.needsDoing': 'Needs doing',
  'facts.line.dependents': { one: '{count} dependent', other: '{count} dependents' },
  'facts.line.backPressure': {
    one: '{count} ready dependent waiting',
    other: '{count} ready dependents waiting',
  },
  'facts.line.scheduleConflict': 'Dependency arrives after end date {end}',
  'facts.line.scheduleConflictBoth': 'Dependency arrives {arrives} after end date {end}',
  'facts.line.committedButInert': {
    one: 'Active, inactive {count} day',
    other: 'Active, inactive {count} days',
  },
  'facts.line.runningOnOldIntent': {
    one: 'Goal untended {count} day',
    other: 'Goal untended {count} days',
  },
  'facts.line.unattendedRisk': {
    one: '{count} ready dependent waiting, unattended',
    other: '{count} ready dependents waiting, unattended',
  },
  'facts.line.tagged': 'Carries #{tag}',
  'facts.line.inStage': 'In {stage}',
  /** A statement's provenance chain (HS-F2). */
  'facts.provenance.seats': 'mirrored from the seats',
  'facts.provenance.flowRule': 'per {flow}: {condition}',
  'facts.provenance.boardRule': "per this board's rule: {condition} = {grade}",
  'facts.provenance.declaredBy': 'declared by {principal}',
  'facts.provenance.cycle': 'computed from the dependency edges',
  'facts.provenance.relayed': 'relayed across a dependency',
  /** A grade's word inside a provenance line. */
  'facts.grade.notice': 'notice',
  'facts.grade.warning': 'warning',
  'facts.grade.critical': 'critical',
  /** The neighbourhood digest's lines (src/read/digest.ts). */
  'facts.digest.deadlock': 'A dependency deadlock',
  'facts.digest.deadlockUnknown': 'A dependency deadlock with unknown participants',
  'facts.digest.cycle': 'A dependency structural cycle',
  'facts.digest.cycleUnknown': 'A dependency structural cycle with unknown participants',
  'facts.digest.relayed': '{origin}: {statement}',
  'facts.digest.anItem': 'an item',
  'facts.digest.aStatement': 'a statement',
  'facts.digest.hidden': "an item you can't see",
} as const;
